import { createAppStore } from '@app/core/stores';
import { User } from '@mi-front/core';
import { Client } from '@app/core/types/client';

const MAX_RECENT_USERS = 10;

type RecentUser = (User & { client: Client }) & {
  lastUsed: number;
};

interface ImpersonationState {
  // Current impersonated user (replaces Redux state)
  impersonatedUser: (User & { client: Client }) | null;

  // Recent users list (replaces localStorage)
  recentUsers: RecentUser[];

  // Actions
  setImpersonatedUser: (user: (User & { client: Client }) | null) => void;
  addRecentUser: (user: User & { client: Client }) => void;
  removeRecentUser: (userId: string) => void;
  clearImpersonatedUser: () => void;
  clearAll: () => void;
}

/** Persisted slice of state (data only; actions are not persisted). */
type PersistedImpersonationState = Pick<ImpersonationState, 'impersonatedUser' | 'recentUsers'>;

export const useImpersonationStore = createAppStore<
  ImpersonationState,
  PersistedImpersonationState
>('ImpersonationStore', {
  persist: {
    name: 'app.admin.impersonation',
    version: 1,
    partialize: (state): PersistedImpersonationState => ({
      impersonatedUser: state.impersonatedUser,
      recentUsers: state.recentUsers,
    }),
  },
})((set, get) => ({
  // Initial state
  impersonatedUser: null,
  recentUsers: [],

  // Set the current impersonated user
  setImpersonatedUser: (user) => {
    set({ impersonatedUser: user });

    // If setting a user, also add to recent users
    if (user) {
      get().addRecentUser(user);
    }
  },

  // Add user to recent users list
  addRecentUser: (user) => {
    const now = Date.now();
    const userWithTimestamp: RecentUser = { ...user, lastUsed: now };

    set((state) => {
      // Remove user if already exists
      const filtered = state.recentUsers.filter((u) => u.id !== user.id);

      // Add user to the beginning and limit to MAX_RECENT_USERS
      const updated = [userWithTimestamp, ...filtered].slice(0, MAX_RECENT_USERS);

      return { recentUsers: updated };
    });
  },

  // Remove a specific user from recent users list
  removeRecentUser: (userId) => {
    set((state) => ({
      recentUsers: state.recentUsers.filter((u) => u.id !== userId),
    }));
  },

  // Clear only the current impersonated user (for logout)
  // Recent users should persist across sessions for convenience
  clearImpersonatedUser: () => {
    set({
      impersonatedUser: null,
    });
  },

  // Clear all data (for complete reset if needed)
  clearAll: () => {
    set({
      impersonatedUser: null,
      recentUsers: [],
    });
  },
}));
